/**
 * Gli strumenti sulla tela, con il tasto scritto accanto (GI-265).
 *
 * **Il suggerimento del tasto sta accanto all'azione, sempre — non solo nella
 * guida (§ 6.3).** `B`, `P`, `Z`, `X`, `C` e lo spazio esistevano gia' come tasti,
 * ma sulla tela non c'era nessun comando: si imparavano aprendo la mappa con
 * `?`, e chi non la apriva disegnava il riquadro col pannello di destra, a
 * mezzo metro dal fotogramma. Qui ogni strumento e' un bottone con il glifo, il
 * nome e il tasto, in alto a sinistra del fotogramma, e produce la **stessa**
 * `Action` che produce il tasto: due strade divergono, e la differenza si
 * scopre dal giornale.
 *
 * **Il modo acceso si vede due volte.** Lo strumento premuto porta
 * `aria-pressed`, e sotto la palette una riga dice che cosa sta succedendo —
 * «3 vertici · Invio chiude» — perche' un disegno a meta' che non si vede e' un
 * disegno che si dimentica, e il clic successivo posa un vertice dove si voleva
 * spostare l'immagine.
 *
 * **In alto a sinistra e non in mezzo.** Il codice di disegno vuole i quattro
 * angoli del fotogramma e niente al centro (§ 6.2): a destra le etichette, in
 * basso il modo e il navigatore.
 */
import { useState } from 'react'

import { useT } from '../i18n/LocaleProvider'
import type { Action } from '../keys'
import type { Shape } from '../Viewer'

export interface CanvasToolsProps {
  /** Che cosa si sta disegnando, o nulla. */
  drawing: Shape | null
  /** Se i disegni sono nascosti (spazio tenuto premuto, o il bottone). */
  hidden: boolean
  /** Quanti vertici del contorno sono gia' posati. */
  vertices: number
  /** Senza fotogramma non c'e' niente su cui disegnare: gli strumenti restano
   *  visibili e spenti, con la ragione nel titolo. */
  disabled: boolean
  /** Se il disegno ha senso qui: su un fotogramma guardato senza candidato non
   *  c'e' un riquadro da correggere, e i due strumenti di disegno si spengono. */
  drawable: boolean
  /** Il contorno si offre dove il contorno si salva: la ricerca libera
   *  dichiara solo riquadri, e uno strumento che non porta a niente e' una
   *  domanda senza risposta. */
  withPolygon?: boolean
  /** «A fuoco sulla trovata» ha senso solo dove c'e' una trovata. */
  withFinding?: boolean
  /** Che cosa fanno il trascinamento e la rotella quando nessuno strumento e'
   *  acceso: si scrive sotto la palette, perche' un gesto che nessuno spiega
   *  e' un gesto che nessuno prova (visto dal vivo nella ricerca libera). */
  hint?: string | null
  onAction: (action: Action) => void
}

interface Tool {
  action: Action
  glyph: string
  key: string
  label: string
  pressed?: boolean
  disabled?: boolean
}

export function CanvasTools({
  drawing,
  hidden,
  vertices,
  disabled,
  drawable,
  withPolygon = true,
  withFinding = true,
  hint = null,
  onAction,
}: CanvasToolsProps): JSX.Element {
  const t = useT()
  const tools: Tool[] = [
    {
      action: { kind: 'draw' },
      glyph: '▭',
      key: 'B',
      label: t('cockpit.toolBox'),
      pressed: drawing === 'box',
      disabled: !drawable,
    },
    ...(withPolygon
      ? [
          {
            action: { kind: 'polygon' } as Action,
            glyph: '⬠',
            key: 'P',
            label: t('cockpit.toolPolygon'),
            pressed: drawing === 'polygon',
            disabled: !drawable,
          },
        ]
      : []),
    { action: { kind: 'zoom', how: 'uno-a-uno' }, glyph: '1:1', key: 'Z', label: t('cockpit.toolOne') },
    { action: { kind: 'zoom', how: 'inquadra' }, glyph: '⤢', key: 'X', label: t('cockpit.toolFit') },
    ...(withFinding
      ? [
          {
            action: { kind: 'zoom', how: 'trovata' } as Action,
            glyph: '◎',
            key: 'C',
            label: t('cockpit.toolFinding'),
            disabled: !drawable,
          },
        ]
      : []),
    {
      // Dal bottone e' un interruttore; dal tasto si tiene premuto. Lo stesso
      // stato, due gesti: il puntatore non ha un «tenere premuto» comodo.
      action: { kind: 'overlays', visible: hidden },
      glyph: hidden ? '◌' : '◉',
      key: t('cockpit.keySpace'),
      label: t('cockpit.toolHide'),
      pressed: hidden,
    },
  ]

  // Lo strumento sotto il puntatore o col fuoco: il suo nome va nella riga di
  // aiuto, dove prima stava solo nel `title=` — che per chi tabula o tocca non
  // c'e' (GI-281).
  const [named, setNamed] = useState<string | null>(null)

  const status =
    drawing === 'polygon'
      ? t('cockpit.drawingPolygon', { n: vertices })
      : drawing === 'box'
        ? t('cockpit.drawingBox')
        : null

  return (
    <div className="cockpit-strumenti" role="toolbar" aria-label={t('cockpit.tools')}>
      <div className="cockpit-strumenti-fila">
        {tools.map((tool) => (
          <button
            key={tool.key}
            type="button"
            className="cockpit-strumento"
            aria-pressed={tool.pressed === undefined ? undefined : tool.pressed}
            aria-label={`${tool.label} (${tool.key})`}
            disabled={disabled || tool.disabled === true}
            onClick={() => onAction(tool.action)}
            onMouseEnter={() => setNamed(`${tool.label} · ${tool.key}`)}
            onMouseLeave={() => setNamed(null)}
            onFocus={() => setNamed(`${tool.label} · ${tool.key}`)}
            onBlur={() => setNamed(null)}
          >
            <span className="cockpit-strumento-glifo" aria-hidden="true">
              {tool.glyph}
            </span>
            <kbd aria-hidden="true">{tool.key}</kbd>
          </button>
        ))}
      </div>
      {status !== null && (
        <p className="cockpit-strumenti-stato" role="status">
          {status}
        </p>
      )}
      {status === null && disabled && <p className="cockpit-strumenti-aiuto">{t('cockpit.toolsNoFrame')}</p>}
      {status === null && !disabled && (named ?? hint) !== null && (
        <p className="cockpit-strumenti-aiuto">{named ?? hint}</p>
      )}
    </div>
  )
}
